<template>
  <div>
    <el-table
      v-if="!tableLoading"
      ref="programSummaryTable"
      class="program-summary-table"
      :class="computedTableClass"
      :data="programSummary"
      empty-text="No Data Available"
      header-cell-class-name="table-header-dark"
      row-class-name="table-row"
      :cell-class-name="tableCellClassName"
      @row-click="expandRow"
      @expand-change="handleExpandChange">
      <el-table-column
        prop="programId"
        label="Program ID"
        show-overflow-tooltip
        label-class-name="column-header-double"
        min-width="80">
      </el-table-column>
      <el-table-column
        prop="programName"
        label="Program Description"
        show-overflow-tooltip
        label-class-name="column-header-double"
        min-width="250">
      </el-table-column>
      <el-table-column
        label="Total"
        show-overflow-tooltip
        label-class-name="column-header-double">
        <template slot-scope="scope">
          {{scope.row.programTotal | currency}}
        </template>
      </el-table-column>
      <el-table-column
        label="Characters of Allocation"
        header-align="center"
        label-class-name="table-header-light">
        <el-table-column label="A" show-overflow-tooltip>
          <template slot-scope="scope">
            {{scope.row.programTotalA | currency}}
          </template>
        </el-table-column>
        <el-table-column label="A1" show-overflow-tooltip>
          <template slot-scope="scope">
            {{scope.row.programTotalA1 | currency}}
          </template>
        </el-table-column>
        <el-table-column label="BC" show-overflow-tooltip>
          <template slot-scope="scope">
            {{scope.row.programTotalBC | currency}}
          </template>
        </el-table-column>
        <el-table-column label="F" show-overflow-tooltip>
          <template slot-scope="scope">
            {{scope.row.programTotalF | currency}}
          </template>
        </el-table-column>
        <el-table-column label="T" show-overflow-tooltip>
          <template slot-scope="scope">
            {{scope.row.programTotalT | currency}}
          </template>
        </el-table-column>
        <el-table-column type="expand">
          <template slot-scope="props">
            <LoadingSpinner v-if="isRowLoading(props.row.index)"></LoadingSpinner>
            <el-table
              v-else
              :data="props.row.programAllocations"
              :show-header="false"
              empty-text="No Data Available"
              row-class-name="program-allocation-row"
              @row-click="goToAllocation">
              <el-table-column
                min-width="150"
                show-overflow-tooltip>
                <template slot-scope="scope">
                  Allocation Number: {{scope.row.allocationNumber}}
                </template>
              </el-table-column>
              <el-table-column
                min-width="180"
                show-overflow-tooltip>
                <template slot-scope="scope">
                  Allocation Date: {{scope.row.approvedDate | moment('MM/DD/YYYY')}}
                </template>
              </el-table-column>
              <el-table-column show-overflow-tooltip>
                <template slot-scope="scope">
                  {{scope.row.allocationTotal | currency}}
                </template>
              </el-table-column>
              <el-table-column show-overflow-tooltip>
                <template slot-scope="scope">
                  {{scope.row.allocationTotalA | currency}}
                </template>
              </el-table-column>
              <el-table-column show-overflow-tooltip>
                <template slot-scope="scope">
                  {{scope.row.allocationTotalA1 | currency}}
                </template>
              </el-table-column>
              <el-table-column show-overflow-tooltip>
                <template slot-scope="scope">
                  {{scope.row.allocationTotalBC | currency}}
                </template>
              </el-table-column>
              <el-table-column show-overflow-tooltip>
                <template slot-scope="scope">
                  {{scope.row.allocationTotalF | currency}}
                </template>
              </el-table-column>
              <el-table-column show-overflow-tooltip>
                <template slot-scope="scope">
                  {{scope.row.allocationTotalT | currency}}
                </template>
              </el-table-column>
              <el-table-column type="expand">
                <template slot-scope="props">
                  <el-table
                    :data="props.row.orgAllocations"
                    :show-header="false"
                    empty-text="No Data Available"
                    row-class-name="breakdown-table-row"
                    header-cell-class-name="breakdown-table-header-cell"
                    class="breakdown-table">
                    <el-table-column
                      min-width="330"
                      class-name="black-text"
                      show-overflow-tooltip>
                      <template slot-scope="scope">
                        {{`${scope.row.orgName} (${scope.row.orgId})`}}
                      </template>
                    </el-table-column>
                    <el-table-column show-overflow-tooltip>
                      <template slot-scope="scope">
                        {{scope.row.allocationTotal | currency}}
                      </template>
                    </el-table-column>
                    <el-table-column show-overflow-tooltip>
                      <template slot-scope="scope">
                        {{scope.row.allocationA | currency}}
                      </template>
                    </el-table-column>
                    <el-table-column show-overflow-tooltip>
                      <template slot-scope="scope">
                        {{scope.row.allocationA1 | currency}}
                      </template>
                    </el-table-column>
                    <el-table-column show-overflow-tooltip>
                      <template slot-scope="scope">
                        {{scope.row.allocationBC | currency}}
                      </template>
                    </el-table-column>
                    <el-table-column show-overflow-tooltip>
                      <template slot-scope="scope">
                        {{scope.row.allocationF | currency}}
                      </template>
                    </el-table-column>
                    <el-table-column show-overflow-tooltip>
                      <template slot-scope="scope">
                        {{scope.row.allocationT | currency}}
                      </template>
                    </el-table-column>
                    <el-table-column type="expand"></el-table-column>
                  </el-table>
                </template>
              </el-table-column>
            </el-table>
          </template>
        </el-table-column>
      </el-table-column>
    </el-table>
    <div v-if="!tableLoading" class="load-more-button-wrapper">
      <el-button
        v-if="showLoadMore"
        type="primary"
        @click="getNextPage"
        :loading="newResultsLoading"
        round
        plain>
        Load More
      </el-button>
    </div>
    <div class="loading-spinner-wrapper">
      <LoadingSpinner v-if="tableLoading" size="60px"></LoadingSpinner>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from '../misc/LoadingSpinner.vue';
import api from '../../utils/api';
import * as Paths from '../../constants/paths';
import Constants from '../../constants';

export default {
  name: 'ProgramSummaryTable',
  components: {
    LoadingSpinner,
  },
  computed: {
    computedTableClass() {
      if (this.isTableHeaderSticky && this.isMenuCollapsed) {
        return 'sticky-header-high';
      }
      if (this.isTableHeaderSticky) {
        return 'sticky-header-low';
      }
      return '';
    },
  },
  data() {
    return {
      programSummary: [],
      tableLoading: false,
      loadingIndex: null,
      expandedCount: 0,
      tableColumns: ['programId', 'programName', 'programTotal', 'programTotalA', 'programTotalA1', 'programTotalBC', 'programTotalF', 'programTotalT'],
      pageNumber: 1,
      resultsPerPage: 20,
      newResultsLoading: false,
      showLoadMore: false,
      isTableHeaderSticky: false,
      isMenuCollapsed: false,
      lastScrollPosition: 0,
      scrollValue: 0,
      tableScrollOffset: Constants.SCROLL_OFFSETS.DASHBOARD_TABLE,
      menuScrollOffset: Constants.SCROLL_OFFSETS.MENU,
    };
  },
  methods: {
    tableCellClassName({
    // eslint-disable-next-line no-unused-vars
      row, column, rowIndex, columnIndex,
    }) {
      const className = ['table-cell'];
      if (this.programSummary[rowIndex][this.tableColumns[columnIndex]] !== 0) {
        className.push('bold-cell');
      }
      return className.join(' ');
    },
    getNextPage() {
      this.pageNumber += 1;
      this.$emit('get-page', this.pageNumber);
    },
    getAndSetProgramSummary(bfy, programId, pageNumber) {
      if (pageNumber === 1) {
        this.pageNumber = 1;
        this.tableLoading = true;
      } else {
        this.newResultsLoading = false;
      }
      api.get(Paths.PROGRAM_SUMMARY_URL(bfy, programId, pageNumber, this.resultsPerPage))
        .then((response) => {
          this.showLoadMore = this.resultsPerPage === response.data.data.length;
          // eslint-disable-next-line max-len
          this.programSummary = pageNumber === 1 ? response.data.data : [...this.programSummary, ...response.data.data];
        }).finally(() => {
          this.tableLoading = false;
          this.newResultsLoading = false;
          this.expandedCount = 0;
        });
    },
    expandRow(row) {
      this.$refs.programSummaryTable.toggleRowExpansion(row);
    },
    handleExpandChange(row, expanded) {
      if (this.expandedCount < expanded.length) {
        this.getProgramAllocations(row.index, row.bfy, row.programId);
      }
      this.expandedCount = expanded.length;
    },
    getProgramAllocations(rowIndex, bfy, programId) {
      this.loadingIndex = rowIndex;
      api.get(Paths.PROGRAM_ALLOCATION_URL(null, bfy, programId)).then((response) => {
        // eslint-disable-next-line no-param-reassign,max-len
        this.programSummary[rowIndex].programAllocations = response.data.data;
      }).finally(() => {
        this.loadingIndex = null;
      });
    },
    goToAllocation(row) {
      this.$router.push(`/allocation/${row.programAllocationId}`);
    },
    isRowLoading(index) {
      return this.loadingIndex === index;
    },
    onScroll() {
      this.isTableHeaderSticky = window.pageYOffset >= this.tableScrollOffset;
      if (!(window.pageYOffset < 0)
        && !(Math.abs(window.pageYOffset - this.lastScrollPosition) < this.menuScrollOffset)) {
        this.isMenuCollapsed = window.pageYOffset >= this.lastScrollPosition;
        this.lastScrollPosition = window.pageYOffset;
      }
    },
  },
  mounted() {
    this.lastScrollPosition = window.pageYOffset;
    window.addEventListener('scroll', this.onScroll);
    const viewportMeta = document.createElement('meta');
    viewportMeta.name = 'viewport';
    viewportMeta.content = 'width=device-width, initial-scale=1';
    document.head.appendChild(viewportMeta);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  },
};
</script>

<style scoped lang="scss">
  .program-summary-table {
    /deep/ .table-row {
      cursor: pointer;
    }
    /deep/ .program-allocation-row {
      background-color: #EEF5F7;
      font-weight: bold;
      color: #2C2C2C;
      cursor: pointer;
      td {
        border-bottom: 1px solid rgba(116, 140, 156, 0.5);
      }
    }
    /deep/ .el-table__expanded-cell {
      text-align: center;
    }
    &.sticky-header-high {
      position: relative;
      /deep/ .el-table__header-wrapper {
        position: fixed;
        top: 26px;
        z-index: 1;
        width: calc(100% - 112px);
      }
    }
    &.sticky-header-low {
      position: relative;
      /deep/ .el-table__header-wrapper {
        position: fixed;
        top: 51px;
        z-index: 1;
        width: calc(100% - 112px);
      }
    }
  }
  .loading-spinner-wrapper {
    text-align: center;
  }
</style>
